import { useEffect, useState } from 'react';
import Select from 'react-select';
import { Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useGetPersons from '@hooks/HR/use-get-persons';
import { Tab, Nav } from 'react-bootstrap';
import useLookupCurrency from '@hooks/Lookups/use-Lookup-Currency';
import { Card, CardBody, CardHeader } from '@components/card/card';
import useLookupPeopleType from '@hooks/Lookups/use-Lookup-PeopleType';
import { useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  UpdatNtxPeoples,
  AddNtxPeoples,
  GetNtxPeoplesByID,
  cleanPeoplesById,
  getSuppliersList
} from '@store/NTX/Peoples/peoplesSlice';
import { SupplierSchema } from '@pages/ValidationForm/validationSchema';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import useGetPaymentMethod from '@hooks/Payrolls/use-Get-PaymentMethod';
import Loading from '@components/Shared/Loader/Loading';
import SupplierItems from './SupplierItems';
import useGetPaymentTermsList from '@hooks/Sales/useGetPaymentTermsList';
import useGetAccounts from '@hooks/Finance/ChartAccountsDetails/useGetAccounts';
import { usePermissions } from '@Permisstions/usePermissions';
import useLookup from '@hooks/Lookups/useLookup';

interface Props {
  Id?: string;
  isUpdate?: boolean;
  onClose?: () => void;
  parentId?: string | any;
  parentName?: string; // اسم المورد الرئيسي
}

const SuppliersForm = ({ Id, isUpdate = false, parentId, parentName, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const { can } = usePermissions();

  const savePermission = isUpdate ? 'PUREditSuppliers' : 'PURAddSuppliers';
  // حالة التبويبات
  const [mainActiveTab, setMainActiveTab] = useState('basic');
  const [subActiveTab, setSubActiveTab] = useState('data-basic');

  // حالة جديدة لتحديد ما إذا كان نوع المورد تم اختياره
  const [supplierTypeSelected, setSupplierTypeSelected] = useState(false);
  const [showTypeSelection, setShowTypeSelection] = useState(!isUpdate);

  // التحقق مما إذا كان يتم إضافة فرع
  const isAddingBranch = !!parentId && !isUpdate;

  // استخدام الهوكس للحصول على البيانات
  const { dataEmployees, loading: personLoading } = useGetPersons();
  const { Currency } = useLookupCurrency();
  const { PaymentTermsList } = useGetPaymentTermsList();
  const { OptionsAccounts, loading: loadingAccounts } = useGetAccounts();
  const { PEOPLE_TYPE } = useLookupPeopleType();
  const { PaymentMethodList } = useGetPaymentMethod();
  const { optionsByType, loading: lookupLoading, error: lookupError } = useLookup(['SupplierCategory']);
  const SupplierCategoryOptions = optionsByType.SupplierCategory;

  // بيانات المورد من الريدكس
  const { PeoplesById, loading } = useAppSelector(state => state.Peoples);

  useEffect(() => {
    dispatch(cleanPeoplesById());
    if (Id && isUpdate) {
      dispatch(GetNtxPeoplesByID(Id));
    }
  }, [Id, dispatch, isUpdate]);

  // تأثير للتحقق من نوع المورد عند تحميل البيانات
  useEffect(() => {
    if (PeoplesById && isUpdate) {
      if (PeoplesById.peopleType) {
        setSupplierTypeSelected(true);
        setShowTypeSelection(false);
      } else {
        setSupplierTypeSelected(false);
        setShowTypeSelection(true);
      }
    }

    // إذا كان يتم إضافة فرع، اختيار النوع تلقائياً
    if (isAddingBranch) {
      setShowTypeSelection(true);
    }
  }, [PeoplesById, isUpdate, isAddingBranch]);

  // تهيئة Formik مع دعم parentId
  const formik = useFormik({
    initialValues: {
      id: PeoplesById?.id ?? null,
      name: PeoplesById?.name ?? '',
      name2: PeoplesById?.name2 ?? '',
      personId: PeoplesById?.personId ?? null,
      typeCode: 'SUPPLIERS',
      customerNumber: PeoplesById?.customerNumber ?? '',
      parentId: parentId || PeoplesById?.parentId || null, // إعطاء الأولوية لـ parentId الممرر
      priceListId: PeoplesById?.priceListId ?? '',
      shipToLocationId: PeoplesById?.shipToLocationId ?? '',
      billToLocationId: PeoplesById?.billToLocationId ?? '',
      termsId: PeoplesById?.termsId ?? '',
      setOfBooksId: PeoplesById?.setOfBooksId ?? '',
      creditLimit: PeoplesById?.creditLimit ?? 0,
      paymentPriority: PeoplesById?.paymentPriority ?? '',
      invoiceCurrencyCode: PeoplesById?.invoiceCurrencyCode ?? 'SAR',
      paymentCurrencyCode: PeoplesById?.paymentCurrencyCode ?? 'SAR',
      invoiceLimit: PeoplesById?.invoiceAmountLimit ?? '',
      paymentMethodId: PeoplesById?.paymentMethodId ?? '',
      paymentMethodLookupCode: PeoplesById?.paymentMethodLookupCode ?? '',
      bankAccountName: PeoplesById?.bankAccountName ?? '',
      bankAccountNum: PeoplesById?.bankAccountNum ?? '',
      bankNumber: PeoplesById?.bankNumber ?? '',
      bankAccountType: PeoplesById?.bankAccountType ?? '',
      vatCode: PeoplesById?.vatCode ?? '',
      tradingName: PeoplesById?.tradingName ?? '',
      workReference: PeoplesById?.workReference ?? '',
      companyRegistrationNumber: PeoplesById?.companyRegistrationNumber ?? '',
      nationalInsuranceNumber: PeoplesById?.nationalInsuranceNumber ?? '',
      verificationNumber: PeoplesById?.verificationNumber ?? '',
      email: PeoplesById?.email ?? '',
      phone: PeoplesById?.phone ?? '',
      fax: PeoplesById?.fax ?? '',
      mobile: PeoplesById?.mobile ?? '',
      address: PeoplesById?.address ?? '',
      description: PeoplesById?.description ?? '',
      extraInformationId: PeoplesById?.extraInformationId ?? '',
      peopleType: PeoplesById?.peopleType ?? '',
      peopleCategory: PeoplesById?.peopleCategory ?? '',
      accountId: PeoplesById?.accountId ?? null,
      prepaymentAccountId: PeoplesById?.prepaymentAccountId ?? null
    },
    enableReinitialize: true,
    validationSchema: SupplierSchema(t),
    onSubmit: values => {
      if (!can(savePermission)) {
        CustomAlert({ action: 'Error', msg: 'لا تملك صلاحية تنفيذ العملية' });
        formik.setSubmitting(false);
        return;
      }

      const requestBody = {
        id: values.id,
        name: values.name,
        name2: values.name2 ?? values.name,
        personId: values.personId || null,
        typeCode: 'SUPPLIERS',
        customerNumber: values.customerNumber,
        parentId: values.parentId || null, // يتم إرسال parentId
        priceListId: values.priceListId || null,
        shipToLocationId: values.shipToLocationId || null,
        billToLocationId: values.billToLocationId || null,
        paymentMethodId: values.paymentMethodId || null,
        termsId: values.termsId || null,
        setOfBooksId: values.setOfBooksId || null,
        creditLimit: values.creditLimit,
        paymentPriority: values.paymentPriority || null,
        invoiceCurrencyCode: values.invoiceCurrencyCode || null,
        paymentCurrencyCode: values.paymentCurrencyCode,
        invoiceLimit: values.invoiceLimit || null,
        paymentMethodLookupCode: values.paymentMethodLookupCode,
        bankAccountName: values.bankAccountName,
        bankAccountNum: values.bankAccountNum,
        bankNumber: values.bankNumber,
        bankAccountType: values.bankAccountType,
        vatCode: values.vatCode,
        tradingName: values.tradingName,
        workReference: values.workReference,
        companyRegistrationNumber: values.companyRegistrationNumber,
        nationalInsuranceNumber: values.nationalInsuranceNumber,
        verificationNumber: values.verificationNumber,
        email: values.email,
        phone: values.phone,
        fax: values.fax,
        mobile: values.mobile,
        address: values.address,
        description: values.description,
        extraInformationId: null,
        integrationField: null,
        peopleType: values.peopleType,
        peopleCategory: values.peopleCategory,
        accountId: values.accountId || null,
        prepaymentAccountId: values.prepaymentAccountId ?? null
      };

      const actionToDispatch = isUpdate ? UpdatNtxPeoples(requestBody) : AddNtxPeoples(requestBody);

      dispatch(actionToDispatch)
        .unwrap()
        .then(res => {
          if (res?.succeeded === true) {
            if (isUpdate) {
              CustomAlert({ action: 'Edit' });
              dispatch(getSuppliersList());
              if (onClose) onClose();
            } else {
              formik.resetForm();
              CustomAlert({ action: 'Add' });
              dispatch(getSuppliersList());
              // إعادة تعيين حالة اختيار النوع بعد الإضافة الناجحة
              setSupplierTypeSelected(false);
              setShowTypeSelection(true);

              // إغلاق النموذج بعد الحفظ
              if (onClose) onClose();
            }
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });

  // دالة لاختيار نوع المورد من الكروت
  const handleSupplierTypeSelect = (supplierType: string) => {
    formik.setFieldValue('peopleType', supplierType);
    setSupplierTypeSelected(true);
    setShowTypeSelection(false);
  };

  // دالة للعودة إلى اختيار النوع
  const handleBackToTypeSelection = () => {
    formik.setFieldValue('peopleType', '');
    setSupplierTypeSelected(false);
    setShowTypeSelection(true);
  };

  // دالة للإلغاء والعودة
  const handleCancel = () => {
    if (onClose) onClose();
  };

  const fieldNames: Record<string, string> = {
    name2: t('Name'),
    peopleType: t('نوع المورد'),
    personId: t('الموظف المرتبط'),
    termsId: t('شروط الدفع'),
    name: t('الاسم العالمي'),
    customerNumber: t('رقم المورد'),
    tradingName: t('الاسم التجاري'),
    invoiceCurrencyCode: t('عملة الفاتورة'),
    vatCode: t('الرقم الضريبي'),
    paymentCurrencyCode: t('عملة الدفع'),
    paymentMethodId: t('طريقة الدفع'),
    accountId: t('الحساب'),
    prepaymentAccountId: t('حساب الدفع المسبق'),
    bankAccountName: t('اسم الحساب البنكي'),
    bankAccountNum: t('رقم الحساب البنكي'),
    bankNumber: t('رقم البنك'),
    bankAccountType: t('نوع الحساب البنكي'),
    companyRegistrationNumber: t('رقم السجل التجاري'),
    nationalInsuranceNumber: t('رقم التأمين الوطني'),
    email: t('البريد الإلكتروني'),
    phone: t('الهاتف'),
    fax: t('الفاكس'),
    mobile: t('الجوال'),
    address: t('العنوان')
  };

  // الحصول على اسم نوع المورد المحدد
  const getSelectedSupplierTypeName = () => {
    const allLists = PEOPLE_TYPE?.flatMap((item: any) => item.list) || [];
    const selectedType = allLists.find((list: any) => list.lookupCode === formik.values.peopleType);
    return selectedType?.meaning2 || 'لم يتم الاختيار';
  };

  // الحصول على أيقونة لنوع المورد
  const getSupplierTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      SUPPLIER: 'fas fa-truck',
      EMPLOYEE: 'fas fa-user-tie',
      COMPANY: 'fas fa-building',
      INDIVIDUAL: 'fas fa-user',
      GOVERNMENT: 'fas fa-landmark',
      ESTABLISHMENT: 'fas fa-store',
      default: 'fas fa-user-circle'
    };
    return icons[type] || icons.default;
  };

  // الحصول على لون لكل نوع
  const getSupplierTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      SUPPLIER: 'primary',
      EMPLOYEE: 'success',
      COMPANY: 'info',
      INDIVIDUAL: 'warning',
      GOVERNMENT: 'secondary',
      ESTABLISHMENT: 'dark',
      default: 'light'
    };
    return colors[type] || colors.default;
  };

  // دالة مساعدة للحصول على وصف لنوع المورد
  const getSupplierTypeDescription = (supplierType: string) => {
    const descriptions: { [key: string]: string } = {
      EMPLOYEE: 'مورد مرتبط بموظف في المنشأة',
      COMPANY: 'مورد يمثل شركة أو منشأة تجارية',
      INDIVIDUAL: 'مورد فردي أو صاحب عمل حر',
      GOVERNMENT: 'مورد حكومي أو جهة رسمية',
      ESTABLISHMENT: 'مؤسسة',
      SUPPLIER: 'مورد عام'
    };

    return descriptions[supplierType] || 'نوع المورد المختار';
  };

  // تصفية أنواع الموردين المتاحة
  const getAvailableSupplierTypes = () => {
    return (
      PEOPLE_TYPE?.flatMap(
        (item: any) =>
          item?.list?.filter(
            (list: any) => list.lookupCode !== 'CUSTOMER' // استبعاد العملاء إذا كان موجوداً
          ) || []
      ) || []
    );
  };

  // التحقق مما إذا كان النموذج جاهزاً للحفظ
  const isFormReady = () => {
    return supplierTypeSelected && formik.values.peopleType;
  };

  return (
    <Loading loading={loading || personLoading || loadingAccounts}>
      {/* رأس الصفحة */}
      <Card className='mb-4 shadow'>
        <CardBody className='p-2'>
          <div className='d-flex align-items-center justify-content-between mb-3'>
            <div className='d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-truck text-theme fa-lg'></i>
              </div>
              <div>
                <h4 className='mb-1 text-theme'>
                  {isAddingBranch
                    ? 'إضافة فرع جديد'
                    : isUpdate
                      ? supplierTypeSelected
                        ? 'تعديل المورد'
                        : 'تحديد نوع المورد'
                      : supplierTypeSelected
                        ? 'إضافة مورد جديد'
                        : 'اختر نوع المورد'}
                </h4>
                <p className='mb-0 text-muted'>
                  {isAddingBranch
                    ? `أضف فرعاً جديداً للمورد: ${parentName}`
                    : showTypeSelection
                      ? 'اختر نوع المورد المناسب للمتابعة'
                      : 'املأ بيانات المورد لإضافته إلى النظام'}
                </p>
              </div>
            </div>

            {/* زر الإلغاء في الهيدر */}
            <div className='d-flex align-items-center'>
              {isUpdate && PeoplesById && supplierTypeSelected && (
                <div className='text-end me-4'>
                  <h5 className='mb-0 text-theme'>{PeoplesById.name}</h5>
                  {PeoplesById.customerNumber && (
                    <small className='text-muted'>رقم المورد: {PeoplesById.customerNumber}</small>
                  )}
                </div>
              )}
              <Button variant='outline-secondary' onClick={handleCancel} className='px-3 py-2'>
                <i className='fas fa-times me-2'></i>
                إلغاء
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* عرض رسالة توضيحية عند إضافة فرع */}

      {/* كارت اختيار نوع المورد */}
      {showTypeSelection && (
        <Card className='mb-4 shadow-sm border-primary'>
          <CardHeader className='bg-primary text-white py-3'>
            <h5 className='mb-0'>
              <i className='fas fa-list-alt me-2'></i>
              {isAddingBranch ? 'اختر نوع الفرع' : isUpdate ? 'تحديد نوع المورد' : 'اختر نوع المورد'}
            </h5>
          </CardHeader>
          <CardBody className='p-4'>
            {/* {isAddingBranch && (
              <div className='alert alert-warning mb-4'>
                <i className='fas fa-link me-2'></i>
                <strong>المورد الرئيسي:</strong> {parentName}
                <br />
                <small className='text-muted'>سيتم ربط هذا الفرع الجديد بالمورد الرئيسي تلقائياً.</small>
              </div>
            )} */}

            <div className='text-center mb-4'>
              <p className='lead text-muted'>
                {isAddingBranch
                  ? 'اختر نوع الفرع المناسب من الخيارات التالية'
                  : isUpdate
                    ? 'لم يتم تحديد نوع هذا المورد سابقاً. يرجى اختيار النوع المناسب للمتابعة.'
                    : 'اختر نوع المورد المناسب من الخيارات التالية'}
              </p>
            </div>

            <div className='row justify-content-center'>
              {getAvailableSupplierTypes().map((list: any) => (
                <div
                  key={list.lookupCode}
                  className='col-xl-3 col-lg-4 col-md-6 mb-4'
                  onClick={() => handleSupplierTypeSelect(list.lookupCode)}
                  style={{
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <Card
                    className={`h-100 cursor-pointer shadow-sm border-2 ${
                      formik.values.peopleType === list.lookupCode ? 'border-primary' : 'border-light'
                    }`}
                  >
                    <CardBody className='text-center p-4'>
                      <div className={`mb-3 text-${getSupplierTypeColor(list.lookupCode)}`}>
                        <i className={`${getSupplierTypeIcon(list.lookupCode)} fa-3x`}></i>
                      </div>
                      <h5 className='card-title mb-2'>{list.meaning2}</h5>
                      <p className='card-text text-muted small'>{getSupplierTypeDescription(list.lookupCode)}</p>
                    </CardBody>
                  </Card>
                </div>
              ))}
            </div>

            {isUpdate && (
              <div className='text-center mt-4'>
                <div className='alert alert-info d-inline-block'>
                  <i className='fas fa-info-circle me-2'></i>
                  يجب تحديد نوع المورد للمتابعة في عملية التعديل
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* باقي النموذج يظهر فقط بعد اختيار نوع المورد */}
      {supplierTypeSelected && !showTypeSelection && (
        <div className='row'>
          {/* القائمة الجانبية */}
          {isUpdate && (
            <div className='col-lg-3 col-md-4 mb-4'>
              <Card className='shadow-sm'>
                <CardHeader className='border-0 py-3'>
                  <h6 className='mb-0'>
                    <i className='fas fa-list me-2 text-theme'></i>
                    الأقسام
                  </h6>
                </CardHeader>
                <CardBody className='p-0'>
                  <ListGroup variant='flush' className='rounded'>
                    <ListGroup.Item
                      action
                      active={mainActiveTab === 'basic'}
                      onClick={() => {
                        setMainActiveTab('basic');
                        setSubActiveTab('data-basic');
                      }}
                      className='border-0 d-flex align-items-center py-3'
                    >
                      <i className='fas fa-id-card me-2'></i>
                      بيانات المورد
                    </ListGroup.Item>

                    <ListGroup.Item
                      action
                      active={mainActiveTab === 'items'}
                      onClick={() => setMainActiveTab('items')}
                      className='border-0 d-flex align-items-center py-3'
                    >
                      <i className='fas fa-boxes me-2'></i>
                      أصناف المورد
                    </ListGroup.Item>
                    <ListGroup.Item
                      action
                      active={mainActiveTab === 'contact'}
                      onClick={() => setMainActiveTab('contact')}
                      className='border-0 d-flex align-items-center py-3'
                    >
                      <i className='fas fa-address-book me-2'></i>
                      بيانات تواصـل إضـافية
                    </ListGroup.Item>
                  </ListGroup>
                </CardBody>
              </Card>
            </div>
          )}

          {/* المحتوى الرئيسي */}
          <div className={isUpdate ? 'col-lg-9 col-md-8' : 'col-12'}>
            {/* تبويب البيانات الأساسية */}
            {mainActiveTab === 'basic' && (
              <Card className='shadow-sm'>
                <CardHeader className='bg-theme bg-opacity-50 text-white py-3'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <h5 className='mb-0'>
                      <i className='fas fa-id-card me-2'></i>
                      بيانات المورد
                    </h5>
                  </div>
                </CardHeader>

                <CardBody className='p-4'>
                  {/* التبويبات الداخلية */}
                  <Nav variant='tabs' className='mb-4' activeKey={subActiveTab}>
                    <Nav.Item>
                      <Nav.Link
                        eventKey='data-basic'
                        onClick={() => setSubActiveTab('data-basic')}
                        className={subActiveTab === 'data-basic' ? 'active fw-bold' : 'text-dark1'}
                      >
                        <i className='fas fa-info-circle me-2'></i>
                        المعلومات الأساسية
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey='finance'
                        onClick={() => setSubActiveTab('finance')}
                        className={subActiveTab === 'finance' ? 'active fw-bold' : 'text-dark1'}
                      >
                        <i className='fas fa-money-bill me-2'></i>
                        البيانات المالية
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey='bank'
                        onClick={() => setSubActiveTab('bank')}
                        className={subActiveTab === 'bank' ? 'active fw-bold' : 'text-dark1'}
                      >
                        <i className='fas fa-university me-2'></i>
                        البيانات البنكية
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey='contact'
                        onClick={() => setSubActiveTab('contact')}
                        className={subActiveTab === 'contact' ? 'active fw-bold' : 'text-dark1'}
                      >
                        <i className='fas fa-address-card me-2'></i>
                        التواصل والعنوان
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  <Form onSubmit={formik.handleSubmit}>
                    {/* محتوى التبويبات الداخلية */}
                    <Tab.Content>
                      {/* المعلومات الأساسية */}
                      {subActiveTab === 'data-basic' && (
                        <Tab.Pane active>
                          <div className='row'>
                            {/* إظهار نوع المورد في الأعلى */}
                            <div className='col-12 mb-4'>
                              <Card className='border-info bg-light1'>
                                <CardBody className='py-3'>
                                  <div className='row align-items-center'>
                                    <div className='col-md-6'>
                                      <h6 className='text-info mb-1'>
                                        <i className='fas fa-tag me-2'></i>
                                        نوع المورد المحدد
                                      </h6>
                                      <p className='mb-0 fw-bold text-dark1'>{getSelectedSupplierTypeName()}</p>
                                    </div>
                                    <div className='col-md-6 text-end'>
                                      <Button variant='outline-info' size='sm' onClick={handleBackToTypeSelection}>
                                        <i className='fas fa-edit me-1'></i>
                                        تغيير النوع
                                      </Button>
                                    </div>
                                  </div>

                                  {/* عرض معلومات الفرع إذا كان إضافة فرع */}
                                  {isAddingBranch && (
                                    <div className='mt-3 pt-3 border-top'>
                                      <div className='d-flex align-items-center'>
                                        <i className='fas fa-link text-primary me-2'></i>
                                        <div>
                                          <small className='text-primary'>الفرع مرتبط بـ:</small>
                                          <div className='fw-bold'>{parentName}</div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </CardBody>
                              </Card>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>الاسم المحلي</Form.Label>
                                <span className='text-danger'>*</span>
                                <Form.Control
                                  type='text'
                                  name='name2'
                                  onChange={formik.handleChange}
                                  value={formik.values.name2}
                                  isInvalid={!!(formik.touched.name2 && formik.errors.name2)}
                                />
                                <Form.Control.Feedback type='invalid'>{formik.errors.name2}</Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>الاسم العالمي</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='name'
                                  onChange={formik.handleChange}
                                  value={formik.values.name}
                                  isInvalid={!!(formik.touched.name && formik.errors.name)}
                                />
                                <Form.Control.Feedback type='invalid'>{formik.errors.name}</Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-4 mb-3'>
                              <Form.Group>
                                <Form.Label>رقم المورد</Form.Label>
                                <Form.Control
                                  disabled
                                  type='number'
                                  name='customerNumber'
                                  onChange={formik.handleChange}
                                  value={formik.values.customerNumber}
                                  isInvalid={!!(formik.touched.customerNumber && formik.errors.customerNumber)}
                                />
                                <Form.Control.Feedback type='invalid'>
                                  {formik.errors.customerNumber}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-4 mb-3'>
                              <Form.Group>
                                <Form.Label>الاسم التجاري</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='tradingName'
                                  onChange={formik.handleChange}
                                  value={formik.values.tradingName}
                                  isInvalid={!!(formik.touched.tradingName && formik.errors.tradingName)}
                                  placeholder='أدخل الاسم التجاري للمورد'
                                />
                                <Form.Control.Feedback type='invalid'>
                                  {formik.errors.tradingName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-4 mb-3'>
                              <Form.Group>
                                <Form.Label>تصنيف المورد</Form.Label>
                                <Select
                                  classNamePrefix='react-select'
                                  isLoading={lookupLoading}
                                  isSearchable={true}
                                  isClearable
                                  options={SupplierCategoryOptions}
                                  value={SupplierCategoryOptions?.find(
                                    (f: any) => f.value === formik.values.peopleCategory
                                  )}
                                  onChange={option => formik.setFieldValue('peopleCategory', option?.value)}
                                  placeholder='اختر تصنيف المورد'
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                  }}
                                />
                              </Form.Group>
                            </div>

                            {formik.values.peopleType === 'EMPLOYEE' && (
                              <div className='col-md-6 mb-3'>
                                <Form.Group>
                                  <Form.Label>الموظف المرتبط</Form.Label>
                                  <Select
                                    classNamePrefix='react-select'
                                    isLoading={personLoading}
                                    isSearchable={true}
                                    isClearable
                                    name='personId'
                                    options={dataEmployees}
                                    onChange={option => {
                                      formik.setFieldValue('personId', option === null ? null : option.value);
                                    }}
                                    value={
                                      formik.values.personId
                                        ? dataEmployees?.find((option: any) => option.value === formik.values.personId)
                                        : null
                                    }
                                    placeholder='اختر الموظف '
                                    menuPortalTarget={document.body}
                                    styles={{
                                      menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                  />
                                  {formik.errors.personId && formik.touched.personId && (
                                    <div className='text-danger small mt-1'>{formik.errors.personId}</div>
                                  )}
                                </Form.Group>
                              </div>
                            )}
                          </div>
                        </Tab.Pane>
                      )}

                      {/* البيانات المالية */}
                      {subActiveTab === 'finance' && (
                        <Tab.Pane active>
                          <div className='row'>
                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>عملة الفاتورة</Form.Label>
                                <Form.Select
                                  name='invoiceCurrencyCode'
                                  onChange={formik.handleChange}
                                  value={formik.values.invoiceCurrencyCode || ''}
                                  isInvalid={
                                    !!(formik.touched.invoiceCurrencyCode && formik.errors.invoiceCurrencyCode)
                                  }
                                >
                                  {Currency &&
                                    Currency?.map((item: any, idx: any) => (
                                      <option key={++idx} value={item.currencyCode}>
                                        {item.description}
                                      </option>
                                    ))}
                                </Form.Select>
                              </Form.Group>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <div className='form-group mb-3'>
                                <label className='form-label'>{t('VATCode')}</label>
                                <Form.Control
                                  className='form-control'
                                  type='string'
                                  name='vatCode'
                                  onChange={formik.handleChange}
                                  value={formik.values.vatCode}
                                  isInvalid={!!(formik.touched.vatCode && formik.errors.vatCode)}
                                />
                                <Form.Control.Feedback type='invalid'>{formik.errors.vatCode}</Form.Control.Feedback>
                              </div>
                            </div>
                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>عملة الدفع</Form.Label>
                                <Form.Select
                                  name='paymentCurrencyCode'
                                  onChange={formik.handleChange}
                                  value={formik.values.paymentCurrencyCode || ''}
                                  isInvalid={
                                    !!(formik.touched.paymentCurrencyCode && formik.errors.paymentCurrencyCode)
                                  }
                                >
                                  {Currency &&
                                    Currency?.map((item: any, idx: any) => (
                                      <option key={++idx} value={item.currencyCode}>
                                        {item.description}
                                      </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type='invalid'>
                                  {formik.errors.paymentCurrencyCode}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>طريقة الدفع</Form.Label>
                                <Form.Select
                                  name='paymentMethodId'
                                  onChange={formik.handleChange}
                                  value={formik.values.paymentMethodId}
                                  isInvalid={!!formik.errors.paymentMethodId}
                                >
                                  <option value=''>اختر طريقة الدفع</option>
                                  {PaymentMethodList?.map((item: any) => (
                                    <option key={item.id} value={item.id}>
                                      {item.name2}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control.Feedback type='invalid'>
                                  {formik.errors.paymentMethodLookupCode}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <div className='form-group mb-3'>
                                <label className='form-label'>شروط الدفع</label>
                                <span className='text-danger'>*</span>
                                <Form.Select
                                  className='form-select'
                                  name='termsId'
                                  onChange={formik.handleChange}
                                  value={formik.values.termsId}
                                  isInvalid={!!(formik.errors.termsId && formik.touched.termsId)}
                                >
                                  <option value={''}>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
                                  {PaymentTermsList &&
                                    PaymentTermsList?.map((item: any, idx: number) => (
                                      <option key={++idx} value={item.id}>
                                        {i18n.language === 'ar' ? item.name2 : item.name}
                                      </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type='invalid'>{formik.errors.termsId}</Form.Control.Feedback>
                              </div>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>الحساب</Form.Label>
                                <Select
                                  classNamePrefix='react-select'
                                  isLoading={loadingAccounts}
                                  isSearchable={true}
                                  isClearable
                                  options={OptionsAccounts}
                                  value={OptionsAccounts?.find(f => f.value === formik.values.accountId)}
                                  onChange={option => formik.setFieldValue('accountId', option?.value)}
                                  placeholder='اختر الحساب'
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                  }}
                                />
                              </Form.Group>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>حساب الدفع المسبق</Form.Label>
                                <Select
                                  classNamePrefix='react-select'
                                  isLoading={loadingAccounts}
                                  isSearchable={true}
                                  isClearable
                                  options={OptionsAccounts}
                                  value={OptionsAccounts?.find(f => f.value === formik.values.prepaymentAccountId)}
                                  onChange={option => formik.setFieldValue('prepaymentAccountId', option?.value)}
                                  placeholder='اختر حساب الدفع المسبق'
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                  }}
                                />
                              </Form.Group>
                            </div>
                          </div>
                        </Tab.Pane>
                      )}

                      {/* البيانات البنكية */}
                      {subActiveTab === 'bank' && (
                        <Tab.Pane active>
                          <div className='row'>
                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>اسم الحساب البنكي</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='bankAccountName'
                                  onChange={formik.handleChange}
                                  value={formik.values.bankAccountName}
                                  isInvalid={!!(formik.touched.bankAccountName && formik.errors.bankAccountName)}
                                  placeholder='أدخل اسم الحساب البنكي'
                                />
                                <Form.Control.Feedback type='invalid'>
                                  {formik.errors.bankAccountName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>رقم الحساب البنكي</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='bankAccountNum'
                                  onChange={formik.handleChange}
                                  value={formik.values.bankAccountNum}
                                  isInvalid={!!(formik.touched.bankAccountNum && formik.errors.bankAccountNum)}
                                  placeholder='أدخل رقم الحساب البنكي'
                                />
                                <Form.Control.Feedback type='invalid'>
                                  {formik.errors.bankAccountNum}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>رقم البنك</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='bankNumber'
                                  onChange={formik.handleChange}
                                  value={formik.values.bankNumber}
                                  isInvalid={!!(formik.touched.bankNumber && formik.errors.bankNumber)}
                                  placeholder='أدخل رقم البنك'
                                />
                                <Form.Control.Feedback type='invalid'>{formik.errors.bankNumber}</Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>نوع الحساب البنكي</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='bankAccountType'
                                  onChange={formik.handleChange}
                                  value={formik.values.bankAccountType}
                                  isInvalid={!!(formik.touched.bankAccountType && formik.errors.bankAccountType)}
                                  placeholder='أدخل نوع الحساب البنكي'
                                />
                                <Form.Control.Feedback type='invalid'>
                                  {formik.errors.bankAccountType}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </div>
                          </div>
                        </Tab.Pane>
                      )}

                      {/* التواصل والعنوان */}
                      {subActiveTab === 'contact' && (
                        <Tab.Pane active>
                          <div className='row'>
                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>رقم السجل التجاري</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='companyRegistrationNumber'
                                  onChange={formik.handleChange}
                                  value={formik.values.companyRegistrationNumber}
                                  isInvalid={
                                    !!(
                                      formik.touched.companyRegistrationNumber &&
                                      formik.errors.companyRegistrationNumber
                                    )
                                  }
                                  placeholder='أدخل رقم السجل التجاري'
                                />
                                <Form.Control.Feedback type='invalid'>
                                  {formik.errors.companyRegistrationNumber}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-6 mb-3'>
                              <Form.Group>
                                <Form.Label>رقم التأمين الوطني</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='nationalInsuranceNumber'
                                  onChange={formik.handleChange}
                                  value={formik.values.nationalInsuranceNumber}
                                  isInvalid={
                                    !!(formik.touched.nationalInsuranceNumber && formik.errors.nationalInsuranceNumber)
                                  }
                                  placeholder='أدخل رقم التأمين الوطني'
                                />
                                <Form.Control.Feedback type='invalid'>
                                  {formik.errors.nationalInsuranceNumber}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </div>

                            <div className='col-md-4 mb-3'>
                              <Form.Group>
                                <Form.Label>البريد الإلكتروني</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='email'
                                  onChange={formik.handleChange}
                                  value={formik.values.email}
                                  isInvalid={!!(formik.touched.email && formik.errors.email)}
                                  placeholder='أدخل البريد الإلكتروني'
                                />
                                <Form.Control.Feedback type='invalid'>{formik.errors.email}</Form.Control.Feedback>
                              </Form.Group>
                            </div>
                            <div className='col-md-4 mb-3'>
                              <Form.Group>
                                <Form.Label>الهاتف</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='phone'
                                  onChange={formik.handleChange}
                                  value={formik.values.phone}
                                  isInvalid={!!(formik.touched.phone && formik.errors.phone)}
                                  placeholder='أدخل رقم الهاتف'
                                />
                                <Form.Control.Feedback type='invalid'>{formik.errors.phone}</Form.Control.Feedback>
                              </Form.Group>
                            </div>
                            <div className='col-md-4 mb-3'>
                              <Form.Group>
                                <Form.Label>الفاكس</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='fax'
                                  onChange={formik.handleChange}
                                  value={formik.values.fax}
                                  isInvalid={!!(formik.touched.fax && formik.errors.fax)}
                                  placeholder='أدخل رقم الفاكس'
                                />
                                <Form.Control.Feedback type='invalid'>{formik.errors.fax}</Form.Control.Feedback>
                              </Form.Group>
                            </div>
                            <div className='col-md-4 mb-3'>
                              <Form.Group>
                                <Form.Label>الجوال</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='mobile'
                                  onChange={formik.handleChange}
                                  value={formik.values.mobile}
                                  isInvalid={!!(formik.touched.mobile && formik.errors.mobile)}
                                  placeholder='أدخل رقم الجوال'
                                />
                                <Form.Control.Feedback type='invalid'>{formik.errors.mobile}</Form.Control.Feedback>
                              </Form.Group>
                            </div>
                            <div className='col-md-8 mb-3'>
                              <Form.Group>
                                <Form.Label>العنوان</Form.Label>
                                <Form.Control
                                  type='text'
                                  name='address'
                                  onChange={formik.handleChange}
                                  value={formik.values.address}
                                  isInvalid={!!(formik.touched.address && formik.errors.address)}
                                  placeholder='أدخل العنوان'
                                />
                                <Form.Control.Feedback type='invalid'>{formik.errors.address}</Form.Control.Feedback>
                              </Form.Group>
                            </div>
                          </div>
                        </Tab.Pane>
                      )}
                    </Tab.Content>

                    {Object.keys(formik.errors).length > 0 && (
                      <div className='alert alert-danger'>
                        <ul className='mb-0'>
                          {Object.entries(formik.errors).map(([field, error]: [string, any]) => (
                            <li key={field}>
                              <strong>{fieldNames[field] ?? field}:</strong> {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* زر الحفظ */}
                    <div className='d-flex justify-content-end mt-4 pt-3 border-top'>
                      <Button
                        variant='theme'
                        type='submit'
                        disabled={formik.isSubmitting || !isFormReady()}
                        className='px-4 py-2'
                      >
                        {formik.isSubmitting ? (
                          <>
                            <Spinner animation='border' size='sm' className='me-2' />
                            جاري الحفظ...
                          </>
                        ) : (
                          <>
                            <i className='fas fa-save me-2'></i>
                            {isAddingBranch ? 'إضافة الفرع' : isUpdate ? 'تحديث المورد' : 'إضافة مورد'}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            )}

            {/* تبويب الأصناف */}
            {mainActiveTab === 'items' && (
              <Card className='shadow-sm border-success'>
                <CardHeader className='bg-theme bg-opacity-50 text-white py-3'>
                  <h5 className='mb-0'>
                    <i className='fas fa-cubes me-2'></i>
                    أصناف المورد
                  </h5>
                </CardHeader>
                <CardBody>
                  <SupplierItems peopleId={Id} />
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      )}
    </Loading>
  );
};
export default SuppliersForm;
